// SEQ-182: extracted out of ProjectStatusTab.tsx unchanged (byte-identical
// behaviour) so PnlManualMetricRows.tsx (the P&L tab's operational
// band) can reuse the exact same tooltip treatment as every other info icon
// on the tab, without creating a circular import (ProjectStatusTab.tsx ->
// PnlManualMetricRows.tsx -> ProjectStatusTab.tsx). A shared component gets
// a shared home instead.
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

export function InfoTooltip({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const TOOLTIP_W = 280;

  const open = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      // Clamp so tooltip doesn't overflow right edge
      const clampedLeft = Math.min(centerX, window.innerWidth - TOOLTIP_W / 2 - 12);
      // Clamp so tooltip doesn't overflow left edge
      const finalLeft = Math.max(clampedLeft, TOOLTIP_W / 2 + 12);
      setPos({ top: rect.bottom + 8, left: finalLeft });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Reposition on scroll (table might be scrolled while tooltip is open)
  useEffect(() => {
    if (!isOpen || !btnRef.current) return;
    const reposition = () => {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const clampedLeft = Math.min(centerX, window.innerWidth - TOOLTIP_W / 2 - 12);
        const finalLeft = Math.max(clampedLeft, TOOLTIP_W / 2 + 12);
        setPos({ top: rect.bottom + 8, left: finalLeft });
      }
    };
    window.addEventListener("scroll", reposition, true);
    return () => window.removeEventListener("scroll", reposition, true);
  }, [isOpen]);

  return (
    <div style={{ display: "inline-flex", position: "relative", verticalAlign: "middle" }}>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); if (isOpen) { setIsOpen(false); } else { open(); } }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          display: "inline-flex",
          alignItems: "center",
          opacity: isOpen ? 1 : 0.45,
          transition: "opacity 0.15s, color 0.15s",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
        onMouseLeave={(e) => { if (!isOpen) (e.currentTarget as HTMLElement).style.opacity = "0.45"; }}
        aria-label="More info"
      >
        <HelpCircle size={13} color={isOpen ? "#1e73be" : "#94a3b8"} />
      </button>
      {isOpen && pos && createPortal(
        <div
          ref={ref}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: "translateX(-50%)",
            background: "#0f172a",
            color: "#e2e8f0",
            fontSize: 12,
            lineHeight: 1.5,
            padding: "10px 14px",
            borderRadius: 10,
            width: TOOLTIP_W,
            whiteSpace: "normal",
            wordWrap: "break-word",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            zIndex: 99999,
            animation: "fadeInUp 0.15s ease",
          }}
        >
          <div style={{ position: "absolute", top: -5, left: "50%", transform: "translateX(-50%)", width: 10, height: 10, background: "#0f172a", rotate: "45deg", borderRadius: 2 }} />
          {text}
        </div>,
        document.body
      )}
    </div>
  );
}
